import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Space, Input, List, Tag, message, Spin, Switch } from 'antd';
import { ArrowLeftOutlined, ScanOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { http } from '../utils/http';

const { Title, Text } = Typography;

const ScanPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scannedCodes, setScannedCodes] = useState([]);
  const [currentCode, setCurrentCode] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [manualScanMode, setManualScanMode] = useState(false);
  const [pendingAppendIndex, setPendingAppendIndex] = useState(null); // 待追加的行索引
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);
  
  // 从URL参数获取数据
  const outstorageOrderDetailId = searchParams.get('outstorageOrderDetailId');
  const stockRecordId = searchParams.get('stockRecordId');
  const batch = searchParams.get('batch');
  const wareHouseName = searchParams.get('wareHouseName');
  const areaName = searchParams.get('areaName');
  const shelfName = searchParams.get('shelfName');

  useEffect(() => {
    if (stockRecordId && !initialized) {
      loadExistingCodes();
      setInitialized(true);
    }
  }, [stockRecordId, initialized]);

  // 页面加载后聚焦输入框
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current && !loading) {
        inputRef.current.focus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [loading]);

  // 监听软键盘显示/隐藏
  useEffect(() => {
    const handleResize = () => {
      // 检测视口高度变化来判断软键盘状态
      const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const windowHeight = window.screen.height;
      const heightDiff = windowHeight - viewportHeight;
      
      // 如果高度差超过150px，认为软键盘弹出
      const isKeyboardVisible = heightDiff > 150;
      setKeyboardVisible(isKeyboardVisible);
      
      // 只在软键盘弹出时且非手动扫码模式下才滚动
      if (isKeyboardVisible && !manualScanMode) {
        setTimeout(() => {
          window.scrollBy({
            top: 200, // 向下滚动200px
            behavior: 'smooth'
          });
        }, 100);
      }
    };

    // 监听视口变化
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [manualScanMode]); // 添加 manualScanMode 依赖

  // 处理输入变化
  const handleInputChange = (e) => {
    setCurrentCode(e.target.value);
  };

  // 处理回车事件
  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter' && !manualScanMode) {
      handleAddCode();
    }
  };

  // 处理输入框聚焦
  const handleInputFocus = () => {
    setKeyboardVisible(true);
    // 只在非手动扫码模式下才自动滚动
    if (!manualScanMode) {
      // 聚焦时向下滚动以展示结果列表
      setTimeout(() => {
        window.scrollBy({
          top: 200, // 向下滚动200px
          behavior: 'smooth'
        });
      }, 300); // 等待软键盘动画完成
    }
  };

  // 处理输入框失焦
  const handleInputBlur = () => {
    setTimeout(() => {
      setKeyboardVisible(false);
    }, 100);
  };

  // 加载已扫描的码
  const loadExistingCodes = async () => {
    setLoading(true);
    try {
      const response = await http.get('/erp/api/OutstorageOrder/GetYCBoxQRCode', {
        params: { stockRecordId }
      });

      if (response.code === 1) {
        setScannedCodes(response.data || []);
      } else {
        message.error(response.message || '获取已扫描码失败');
      }
    } catch (error) {
      console.error('加载已扫描码失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };
  // 添加扫描码
  const handleAddCode = (codeValue = null) => {
    const code = codeValue || currentCode;
    
    if (!code.trim()) {
      message.warning('请输入二维码');
      return;
    }

    // 如果有待追加的行，则拼接到该行
    if (pendingAppendIndex !== null) {
      setScannedCodes(prev => {
        const newCodes = [...prev];
        newCodes[pendingAppendIndex] = newCodes[pendingAppendIndex] + code.trim();
        return newCodes;
      });
      setPendingAppendIndex(null); // 重置状态
      if (!manualScanMode) {
        setCurrentCode('');
      }
      message.success('拼接成功');
      
      if (!manualScanMode) {
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
      }
      return;
    }

    if (scannedCodes.includes(code.trim())) {
      message.warning('该二维码已存在');
      if (!manualScanMode) {
        setCurrentCode('');
      }
      return;
    }

    // 新数据添加到数组开头（最上面）
    setScannedCodes(prev => [code.trim(), ...prev]);
    if (!manualScanMode) {
      setCurrentCode('');
    }
    message.success('添加成功');
    
    // 只在非手动扫码模式下才重新聚焦输入框
    if (!manualScanMode) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  // 删除扫描码
  const handleRemoveCode = (codeToRemove) => {
    setScannedCodes(prev => prev.filter(code => code !== codeToRemove));
    message.success('删除成功');
  };

  // 提交扫描结果
  const handleSubmit = async () => {
    if (scannedCodes.length === 0) {
      message.warning('请至少扫描一个二维码');
      return;
    }

    setSubmitting(true);
    try {
      const response = await http.post('/erp/api/OutstorageOrder/OutstorageOrderYCBoxQRCode', {
        outstorageOrderDetailId,
        stockRecordId,
        batch,
        boxQRCodes: scannedCodes
      });

      if (response.code === 1) {
        message.success('提交成功');
        navigate(-1);
      } else {
        message.error(response.message || '提交失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  // 处理手动添加按钮点击
  const handleManualAdd = () => {
    handleAddCode();
    // 手动扫码模式下，添加成功后清空输入框
    if (manualScanMode) {
      setCurrentCode('');
    }
  };

  // 处理追加按钮点击
  const handleAppendClick = (index) => {
    setPendingAppendIndex(index);
    message.info('请扫描要拼接的二维码');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">加载中...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* 页面头部 */}
      <div className="page-header">
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            type="text"
            size="large"
          />
          <Title level={3} style={{ margin: 0 }}>
            扫码出库
          </Title>
        </Space>
      </div>

      {/* 页面内容 */}
      <div 
        className="page-content" 
        style={{ 
          padding: '0 20px',
          paddingBottom: keyboardVisible ? '350px' : '20px', // 软键盘弹出时增加底部距离
          transition: 'padding-bottom 0.3s ease'
        }}
      >
        {/* 手动扫码开关 */}
        <Card 
          style={{ marginBottom: 24, borderRadius: 12 }}
          bodyStyle={{ padding: '16px 20px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text strong>手动扫码</Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  开启后需手动点击添加按钮
                </Text>
              </div>
            </div>
            <Switch 
              checked={manualScanMode}
              onChange={setManualScanMode}
              size="default"
            />
          </div>
        </Card>
        {/* 出库记录信息 */}
        <Card 
          title="出库记录"
          style={{ marginBottom: 24, borderRadius: 12 }}
          bodyStyle={{ padding: '20px 24px' }}
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div>
              <Text type="secondary">批号: </Text>
              <Text strong>{batch || '-'}</Text>
            </div>
            <div>
              <Text type="secondary">仓库: </Text>
              <Text strong>
                {[wareHouseName, areaName, shelfName].filter(Boolean).join(' / ') || '-'}
              </Text>
            </div>
          </Space>
        </Card>

        {/* 扫码输入 */}
        <Card
          title="扫码输入"
          style={{ marginBottom: 24, borderRadius: 12 }}
          bodyStyle={{ padding: '20px' }}
        >
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Input
              ref={inputRef}
              placeholder="请扫描或输入二维码"
              value={currentCode}
              onChange={handleInputChange}
              onKeyPress={handleInputKeyPress}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              prefix={<ScanOutlined />}
              size="large"
              style={{ 
                flex: 1, 
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                lineHeight: '48px'
              }}
              styles={{
                input: {
                  lineHeight: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
              autoComplete="off"
            />
            <Button 
              type="primary" 
              onClick={handleManualAdd}
              size="large"
              style={{ 
                height: '48px', 
                minWidth: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              添加
            </Button>
          </div>
        </Card>

        {/* 已扫描列表 */}
        <Card
          ref={listRef}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>已扫描二维码</Text>
              <Tag color="blue">{scannedCodes.length} 个</Tag>
            </div>
          }
          style={{ 
            marginBottom: 24, 
            borderRadius: 12
          }}
          bodyStyle={{ 
            padding: '20px',
            minHeight: '400px', // 设置最小高度
            maxHeight: '600px', // 增加最大高度
            overflowY: 'auto'
          }}
        >
          {scannedCodes.length > 0 ? (
            <List
              dataSource={scannedCodes}
              renderItem={(code, index) => (
                <List.Item
                  actions={[
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      style={{ minWidth: 120, minHeight: 80, color: '#52c41a', backgroundColor: '#f6ffed' }}
                      onClick={() => handleAppendClick(index)}
                      disabled={pendingAppendIndex !== null}
                    >
                      添加
                    </Button>,
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      style={{ minWidth: 120, minHeight: 80 }}
                      onClick={() => handleRemoveCode(code)}
                    >
                      删除
                    </Button>
                  ]}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Tag color="green">{scannedCodes.length - index}</Tag>
                    <Text strong>{code}</Text>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Text type="secondary">暂无扫描记录</Text>
            </div>
          )}
        </Card>

        {/* 提交按钮 */}
        { (
          <div style={{ 
            textAlign: 'center', 
            marginBottom: 24,
            paddingBottom: '20px' // 增加距离底部的距离
          }}>
            <Button
              type="primary"
              size="large"
              loading={submitting}
              onClick={handleSubmit}
              disabled={scannedCodes.length === 0}
              style={{ minWidth: 480, minHeight: 100 }}
            >
              提交扫码结果
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanPage;